// Shared flip-up decision for portal-rendered dropdown menus. Solves the
// "menu clips off the bottom of the viewport" bug — a dropdown near the
// bottom of a long list opens BELOW its trigger and gets cut off. With this
// helper, the menu auto-flips upward when there isn't enough room below.
//
// Each caller still owns its `left` (or `right`) coordinate — this helper
// only decides the vertical anchor. The reason: callers vary on horizontal
// alignment (right-aligned to trigger edge, left-aligned, clamped to
// viewport edges, etc.), so factoring left out keeps the helper simple.
//
// Two-part guard:
//   1. We flip up only if there's INSUFFICIENT room below
//   2. AND there's MORE room above
// If above is ALSO cramped (rare — tiny viewport), fall back to anchoring
// below rather than flipping into an equally bad spot.

const DEFAULT_GAP_PX = 4;
const DEFAULT_EDGE_PAD_PX = 12;

/**
 * @param {DOMRect | { top: number, bottom: number }} anchorRect - getBoundingClientRect() of the trigger
 * @param {number} estimatedHeight - menu height in px (over-estimate is OK — a flipped menu that wastes 4-8px is invisible; a no-flip that clips items is the bug)
 * @param {object} [opts]
 * @param {number} [opts.gap=4] - vertical gap between trigger and menu
 * @param {number} [opts.edgePad=12] - min distance from viewport edges
 * @returns {{ top: number, droppedUp: boolean }}
 */
export function computeDropdownTop(anchorRect, estimatedHeight, opts = {}) {
  const gap = opts.gap ?? DEFAULT_GAP_PX;
  const edgePad = opts.edgePad ?? DEFAULT_EDGE_PAD_PX;
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  const shouldFlipUp = spaceBelow < estimatedHeight + edgePad && spaceAbove > spaceBelow;
  const top = shouldFlipUp
    ? Math.max(edgePad, anchorRect.top - estimatedHeight - gap)
    : anchorRect.bottom + gap;
  return { top, droppedUp: shouldFlipUp };
}
